import md5 from 'crypto-js/md5'
import { Auth } from 'aws-amplify'
import { useUser } from '@/lib/hooks'
import logger from '~/lib/logger'
import { Events, EventBus } from '@/lib/events'

export default {
  data: () => ({
    user: null,
    token: null,
    userRoles: [],
    loadingUser: true,
  }),

  async mounted() {
    EventBus.$on(Events.DashboardRefreshUser, this.getUser)
    try {
      await this.getUser()
    } finally {
      this.loadingUser = false
    }
  },

  computed: {
    /**
     * If we have an Authenticated user.
     *
     * @returns {boolean}
     */
    loggedIn() {
      return !!this.user?.id
    },

    /**
     * The plan the user is subscribed to.
     *
     * @returns {object}
     */
    plan() {
      return this.user?.plan || {}
    },

    /**
     * If the user has the Brand Plan.
     *
     * @returns {boolean}
     */
    hasBrandPlan() {
      return (
        this.plan.package?.code === 'account-plan' ||
        this.plan.package?.code === 'brand-plan' ||
        this.plan.package?.code === 'agency-plan'
      )
    },

    /**
     * The full name of the user.
     *
     * @returns {string}
     */
    name() {
      if (!this.loggedIn) {
        return null
      }

      const { given_name, family_name } = this.user || {}

      return `${given_name || ''} ${family_name || ''}`.trim()
    },

    /**
     * Try and fetch an avatar from Gravatar, fallsback to a
     * transparent square to display a generic icon underneath.
     */
    avatar() {
      if (!this.loggedIn) {
        return null
      }

      const hash = md5(this.user?.email).toString()

      return `https://www.gravatar.com/avatar/${hash}?d=404`
    },
  },

  beforeDestroy() {
    EventBus.$off(Events.DashboardRefreshUser, this.getUser)
  },

  methods: {
    async getToken() {
      const user = await Auth.currentAuthenticatedUser()

      this.token = user.signInUserSession.accessToken.jwtToken
    },

    async getUserRoles() {
      const user = await Auth.currentAuthenticatedUser()

      this.userRoles =
        user.signInUserSession.accessToken.payload['cognito:groups'] || []
    },

    async getUser() {
      try {
        const { getUser } = useUser(this.$api)

        this.user = await getUser()
        await Promise.all([this.getToken(), this.getUserRoles()])
      } catch (e) {
        logger.debug('Failed to get user', e)
      }
    },
  },
}
